/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useIntl, useSelector } from 'umi';
import {
  Modal,
  Button,
  Form,
  Input,
  Checkbox,
  notification,
  Radio,
} from 'tea-component';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import inputMap from '@/pages/dashboard/components/NewFilter/FilterBase/FilterList';
import TipContext from '@/pages/home/components/TipContext';
import { getDateVal } from '@/pages/dashboard/utils/utils';
import { debounce } from 'lodash-es';

import '@/pages/dashboard/assets/scss/components/saveFilterSetting.scss';
import renderCustom from '@/pages/dashboard/components/NewFilter/FilterBase/RenderCustomSelect';
import { getSaveValue } from './util';
import { DATE_FILTER_ARR } from './_config';
import { getName } from '@home/utils/common';

export default function SaveFilterSetting(props: any) {
  const {
    visible,
    close,
    formValue: defaultValue,
    selectorSchemeList,
    inputList,
    type,
    editFilter,
    gameConfig,
    addFilterSchemeList,
    editFilterSchemeList,
  } = props;

  const dispatch = useDispatch();
  const { locale, getLocalText, isEN, formatMessage } = useLocal('filter');
  const [errorMessage, setErrorMessage] = useState(''); // 错误校验信息
  const [filterName, setFilterName] = useState('');
  const [formValue, setFormValue] = useState(defaultValue);
  const isDefaultInit = type === 'edit' ? editFilter?.is_default : false;
  const [isDefault, setIsDefault] = useState(isDefaultInit);
  const dateType = type === 'edit' ? (editFilter?.dateType || 'dateSelect') : 'dateDuration';
  const [filterSaveDateType, setFilterSaveDateType] = useState(dateType);
  const durationRef = useRef(null);

  // 方案名称列表
  const filterList = useMemo(() => selectorSchemeList?.map(
    (item: { filter_name: any; }) => item?.filter_name?.trim() || '',
  ), [selectorSchemeList]);

  const { isDisabled, dateFilterArr, otherFilterArr, durationType, dateDurationText, bubbleRangeValue } = useMemo(() => {
    const renderInputList = inputList?.filter((v: any) => v?.visible !== 0) || [];
    const renderInputListKeyArr = renderInputList?.map((v: any) => v?.key) || [];
    // confirm按钮禁用逻辑
    const isDisabled = renderInputListKeyArr?.length === 0;
    // 日期筛选器
    const dateFilterArr =
      renderInputList?.filter((item: { type: string; key: string; }) => {
        const key = item?.type || item?.key;
        return DATE_FILTER_ARR?.includes(key);
      }) || [];
    // 首个日期筛选器
    const firtstDateFilter = dateFilterArr?.[0];
    // 首个日期筛选器key值
    const firstDateKey = firtstDateFilter?.key || firtstDateFilter?.type;
    // 起止时间范围
    const rangeValue = formValue?.[firstDateKey]?.map((date: any) => getDateVal(date));
    // 快捷选项
    const shortcutOption = firtstDateFilter?.shortcutOption || [];
    // 判断选中时间和选项值
    const duration = shortcutOption?.find((v: any) => JSON.stringify(v?.duration()) === JSON.stringify(rangeValue));
    if (duration) {
      durationRef.current = duration;
    }
    // 缓存的快捷选项文本
    const refDateDurationText = getName(isEN, durationRef?.current?.name, durationRef?.current?.nameCN);
    // 默认文本
    const defaultDateDurationText = getName(isEN, shortcutOption?.[0]?.name, shortcutOption?.[0]?.nameCN);
    // Duration的类型
    const durationType = durationRef?.current?.type || shortcutOption?.[0]?.type;
    // 设置Duration的文本
    const dateDurationText = refDateDurationText || defaultDateDurationText || "";

    // 其他筛选器
    const otherFilterArr =
      renderInputList?.filter((item: { type: string; key: string; }) => {
        const key = item?.type || item?.key;
        return !DATE_FILTER_ARR?.includes(key);
      }) || [];

    return {
      isDisabled,
      dateFilterArr,
      otherFilterArr,
      durationType,
      dateDurationText,
      bubbleRangeValue: rangeValue,
    };
  }, [formValue, inputList, isEN]);

  // 编辑弹窗默认赋值逻辑
  useEffect(() => {
    if (type === 'edit') {
      const { filter_name, is_default, value } = editFilter || {};
      setErrorFunc(filter_name);
      setFilterName(filter_name || '');
      // 编辑需要格式化数据（时间和筛选器变化）
      const newValue = getSaveValue(editFilter, inputList);
      setFormValue(newValue);
      setIsDefault(is_default);
    }
  }, [type]);

  const setErrorFunc = (val: string) => {
    const isError = filterList?.includes(val?.trim());
    const isNull = val?.trim() === '';
    if (isNull) {
      setErrorMessage(formatMessage({ id: 'filter_save_enter_a_name' }));
      return;
    }
    const message = type === 'edit' ? formatMessage({ id: 'filter_edit_error_message' }) : formatMessage({ id: 'filter_save_error_message' });
    setErrorMessage(isError ? message : '',);
  };

  const handleNameOnChange = (val: string) => {
    setFilterName(val);
    setErrorFunc(val);
  };

  const handleOnSubmit = () => {
    setErrorFunc(filterName);
    const isNull = filterName?.trim() === '';
    if (isNull || (errorMessage && type === 'add')) {
      return;
    }

    const params: any = {
      filter_name: filterName,
      is_default: isDefault,
      dateType: filterSaveDateType,
      durationType,
      value: getSaveValue({ dateType: filterSaveDateType, durationType, value: formValue }, inputList),
    };

    if (type === 'edit') {
      editFilterSchemeList({ ...params, editFilter }, () => { close?.() });
      return;
    }

    addFilterSchemeList(params, () => { close?.() });

  };

  const onFilterChange = (key: string, value: any) => {
    const newValue = {
      ...formValue,
      [key]: value,
    };
    setFormValue(newValue);
  };

  return (
    <Modal
      visible={visible}
      size={800}
      className="save-filter-setting"
      destroyOnClose
      caption={
        type === 'edit'
          ? getLocalText('edit_setting')
          : getLocalText('save_setting')
      }
      onClose={close}
    >
      <Modal.Body>
        <Form className="setting-table">
          <Form.Item
            label={<div className='dbd-modal-form-lable'>{getLocalText('save_setting_name')}</div>}
            showStatusIcon={false}
            status={
              // eslint-disable-next-line no-nested-ternary
              errorMessage
                ? type === 'edit' && filterList?.includes(filterName?.trim())
                  ? 'validating'
                  : 'error'
                : 'sucuss'
            }
            message={errorMessage}
          >
            <Input
              placeholder={getLocalText('save_enter_a_name')}
              size="full"
              value={filterName}
              onChange={handleNameOnChange}
            />
          </Form.Item>
          <Form.Item showStatusIcon={false}>
            <Checkbox
              value={isDefault}
              onChange={(value) => setIsDefault(value)}
            >
              <div className="dbd-neutral-text">
                {getName(isEN, "Save as default setting", "保存为默认设置")}
              </div>
            </Checkbox>
          </Form.Item>
          {
            dateFilterArr?.length > 0 && <Form.Item
              label={
                <div className='dbd-filter-lable'>
                  {getLocalText('save_time_setting')}
                  <TipContext
                    tipText={
                      <>
                        <p>{getLocalText('save_setting_title')}</p>
                        <p>{getLocalText('save_setting_info_start_end', {
                          start: bubbleRangeValue?.[0],
                          end: bubbleRangeValue?.[1]
                        })}</p>
                        {
                          <p>
                            {getLocalText('save_setting_info_duration', {
                              latestValue: dateDurationText || ''
                            })}
                          </p>
                        }
                      </>
                    }
                  />
                </div>
              }
              showStatusIcon={false}
            >
              <Radio.Group
                value={filterSaveDateType}
                onChange={(value) => setFilterSaveDateType(value)}
                className="dbd-filter-time"
              >
                <div className="dbd-filter-time-item">
                  <Radio name="dateSelect">
                    {dateFilterArr?.map((item: any, index: number) => {
                      const {
                        type,
                        key,
                        render: itemRender,
                        visible,
                        ...rest
                      } = item;
                      const inputVal = formValue[key] || null;
                      const inputDefaultVal = defaultValue[key] || null;
                      const inputType = type || key;
                      const render = itemRender || inputMap[inputType];
                      // 时间对比页面日期保存的时候，不保存对比日期，弹窗内也不展示对比日期
                      const isContrastDateRange = inputType === 'contrastDate';
                      const renderFlag = !render || visible === 0;


                      // 后端接口返回（visible === 0）不展示
                      if (renderFlag || isContrastDateRange) return null;
                      // 维度筛选器
                      return (
                        <div
                          key={inputType + index}
                          className={`setting-group-date-item`}
                        >
                          <div className="setting-flex">
                            <span className="dbd-filter-save-bubble-content">
                              {render({
                                ...rest,
                                key,
                                locale,
                                overlayClassName: 'dbh-range-picker-overly',
                                getLocalText,
                                latestDate: '',
                                value: inputVal,
                                defaultValue: inputDefaultVal,
                                onChange: onFilterChange,
                                // disabled: isRangeDateMonth,
                                dispatch,
                                seasonInfo: gameConfig?.season_info,
                                renderKey: `save_select_${key}`,
                                // timeTabDisabled: isDateFilter
                              })}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </Radio>
                </div>
                {
                  <div className="setting-group-item">
                    <Radio name="dateDuration">
                      <div className='dbd-neutral-lable'>{getLocalText('save_latest_date')}</div> {dateDurationText || ''}
                    </Radio>
                  </div>
                }
              </Radio.Group>
            </Form.Item>
          }
          {
            otherFilterArr?.length > 0 && <Form.Item
              label={<div className='dbd-filter-location'>{getLocalText('save_other_setting')}</div>}
              showStatusIcon={false}
            >
              <div className="setting-group mod-filter">
                {otherFilterArr?.map((item: any, index: number) => {
                  const {
                    type,
                    key,
                    render: itemRender,
                    visible,
                    name_en,
                    name_cn,
                    all_info_en,
                    all_info_cn,
                    ...rest
                  } = item;
                  // 时间筛选器暂不做保存
                  const isDateFilter = DATE_FILTER_ARR?.includes(key);
                  const inputVal = formValue[key] || null;
                  const inputDefaultVal = defaultValue[key] || null;
                  const inputType = type || key;
                  const render = itemRender || inputMap[inputType] || renderCustom;
                  const renderFlag = !render || visible === 0;
                  const name = isEN ? name_en : name_cn;
                  const allInfo = isEN ? all_info_en : all_info_cn;

                  // 后端接口返回（visible === 0）不展示
                  if (renderFlag) return null;
                  // 维度筛选器
                  return (
                    <>
                      <div className="setting-group-item" key={inputType + index}>
                        {render({
                          ...rest,
                          key,
                          locale,
                          getLocalText,
                          latestDate: '',
                          value: inputVal,
                          defaultValue: inputDefaultVal,
                          onChange: onFilterChange,
                          disabled: isDateFilter,
                          timeTabDisabled: isDateFilter,
                          name,
                          allInfo,
                          renderKey: `save_select_${key}`,
                          isPopupContainer: true,
                        })}
                      </div>
                    </>
                  );
                })}
              </div>

            </Form.Item>
          }
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={debounce(handleOnSubmit, 300)} disabled={isDisabled}>
          {formatMessage({ id: 'common_confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}


